// TodayBanner — conditional banner for 「今天是 X 紀念日」 (W3.1 §5.1 Z3 + §5.6).
//
// Renders only when today_anniversaries.items.length > 0. Otherwise null
// (so the layout doesn't reserve vertical space).

import { Link } from "react-router-dom";
import { PartyPopper, Pencil } from "lucide-react";

import type { AnniversaryTodayItem } from "@/lib/api/anniversary";
import { categoryLabelZh } from "@/lib/anniversary/categoryIcon";
import { cn } from "@/lib/utils/cn";

export interface TodayBannerProps {
  items: AnniversaryTodayItem[];
}

export function TodayBanner({ items }: TodayBannerProps) {
  if (!items || items.length === 0) return null;
  const first = items[0]!;
  const title =
    items.length === 1
      ? `今天是「${first.title}」!`
      : `今天有 ${items.length} 個紀念日!`;

  return (
    <section
      data-component="TodayBanner"
      aria-live="polite"
      className={cn(
        // iOS-style: soft tint (10% alpha gold) + thin hairline border so the
        // banner reads as a celebratory accent without overwhelming the
        // hero card beneath it.
        "mx-4 rounded-2xl border border-gold/15 bg-gold-tint p-4",
        "flex flex-col gap-3",
      )}
      data-testid="today-banner"
    >
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gold text-paper"
        >
          <PartyPopper size={20} strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-display text-title-3 text-gold font-semibold">{title}</p>
          {items.length > 1 ? (
            <p className="text-meta text-ink-2 truncate">
              {items.slice(0, 3).map((i) => i.title).join(" · ")}
            </p>
          ) : (
            <p className="text-meta text-ink-2">
              {categoryLabelZh(first.category)}
              {first.is_anchor ? " · 交往紀念日" : ""}
            </p>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <Link
          to={`/diaries/new#source=anniversary&anniversary_id=${first.id}`}
          data-testid="today-banner-write"
          className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-pill bg-paper text-ink-1 text-meta font-semibold press border border-hairline"
        >
          <Pencil size={14} aria-hidden />
          為今天寫日記
        </Link>
        <Link
          to={`/anniversaries/${first.id}`}
          data-testid="today-banner-detail"
          className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-pill bg-gold text-paper text-meta font-semibold press"
        >
          查看詳情
        </Link>
      </div>
    </section>
  );
}